import type { GetServerSidePropsContext } from "next";

import { strapiClient } from "@shared/api";
import { NotFoundError } from "@shared/utils/not-found-error";

import { getGlobal } from "./get-global";
import rebuild from "./rebuild";

type LoadPageWithGlobalFn<T> = (client: typeof strapiClient) => Promise<T>;

type LoadPageWithGlobalResult<T> =
  | { notFound: true }
  | {
      props: {
        global: any;
        cms: T;
      };
    };

/**
 * Страницы рендерятся на каждый запрос, и Next по умолчанию отдаёт для них
 * `Cache-Control: private, no-cache, no-store, max-age=0, must-revalidate`.
 * no-store выключает back/forward cache браузера — отдельный проваленный аудит
 * Lighthouse и заметно более медленный переход «назад» у пользователя.
 *
 * Контент одинаков для всех и авторизации нет: браузер перепроверяет свежесть
 * каждый раз (max-age=0), а промежуточный кэш держит ответ s-maxage секунд и
 * ещё stale-while-revalidate секунд может отдавать устаревший, обновляя его
 * в фоне.
 *
 * Сроки короткие намеренно: правки в Strapi должны доезжать до сайта быстро,
 * автоматического сброса кэша nginx у нас нет. Пятнадцать секунд свежести плюс
 * минута фонового обновления дают потолок отставания около 75 секунд, при этом
 * подавляющее большинство запросов по-прежнему отдаётся из кэша за ~25 мс
 * вместо ~270 мс на рендер.
 *
 * Заголовок ставится только здесь: middleware для страниц с getServerSideProps
 * не работает — Next перезаписывает Cache-Control уже после него (проверено).
 */
export const PAGE_CACHE_CONTROL =
  "public, max-age=0, s-maxage=15, stale-while-revalidate=60";

export const loadPageWithGlobal = async <T>(
  cb: LoadPageWithGlobalFn<T>,
  ctx?: Pick<GetServerSidePropsContext, "res">,
): Promise<LoadPageWithGlobalResult<T>> => {
  ctx?.res?.setHeader("Cache-Control", PAGE_CACHE_CONTROL);

  const startedAt = Date.now();
  let globalMs = 0;
  let pageMs = 0;

  try {
    // Запросы независимы: раньше они шли последовательно и каждая страница
    // платила задержку глобальных данных плюс задержку своих
    const [global, pageResponse] = await Promise.all([
      getGlobal().finally(() => {
        globalMs = Date.now() - startedAt;
      }),
      Promise.resolve(cb(strapiClient)).finally(() => {
        pageMs = Date.now() - startedAt;
      }),
    ]);

    // Видно в devtools во вкладке Timing: сразу понятно, что тормозит в TTFB —
    // глобальные данные или запросы конкретной страницы
    ctx?.res?.setHeader(
      "Server-Timing",
      `global;dur=${globalMs}, page;dur=${pageMs}`,
    );

    // Если callback вернул { notFound: true }, возвращаем как есть
    if (
      pageResponse &&
      typeof pageResponse === "object" &&
      "notFound" in pageResponse
    ) {
      return pageResponse as { notFound: true };
    }

    return {
      props: {
        global: rebuild(global),
        cms: {
          ...pageResponse,
        },
      },
    };
  } catch (error) {
    // Если поймали NotFoundError, возвращаем 404
    if (error instanceof NotFoundError) {
      return { notFound: true };
    }

    // Для других ошибок пробрасываем дальше
    throw error;
  }
};
